import { tw } from "@careinfo/tailwind";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import AddInventoryPopup from "../AddIProductPopup";
import CreateUnit from "../InventoryInfo/CreateUnit";
import ProductGroupForm from "../InventoryInfo/ProductGroup";
import Button from "../Reusable/Button";
import Card from "../Reusable/Card";
import PopupModal from "../Reusable/Modal";
import CreateParty from "./CreateParty";
import Form from "./Form";
import { ServiceSalesSchema, TradingSalesSchema } from "../../validation/Sales";

const AddSales = (props: any) => {
  const {
    handleCreate,
    isLoading,
    numbSeriesRefetch,
    entryNoRefetch,
    openPrintPopup,
    companyData,
    handlePrint,
    setOpenPrintPopup,
    isPrintLoading,
  } = props;
  const navigate = useNavigate();
  const [openUnitFormPopup, setOpenUnitFormPopup] = useState(false);
  const [openCategoryFormPopup, setOpenCategoryFormPopup] = useState(false);
  const [isOpenModalPopup, setIsOpenModalPopup] = useState(false);
  const [isOpenItemsPopup, setIsOpenItemsPopup] = useState(false);
  const {
    control,
    register,
    setValue,
    setError,
    getValues,
    clearErrors,
    watch,
    formState: { errors },
    handleSubmit,
  } = useForm<any>({
    resolver: yupResolver(
      companyData?.type === "Trading" ? TradingSalesSchema : ServiceSalesSchema
    ),
    defaultValues: async () => {
      const countData = await entryNoRefetch();
      const numberSeries = await numbSeriesRefetch();
      const nextCount = (countData?.data?.count ?? 0) + 1;
      const formattedBillNumber = String(nextCount).padStart(5, "0");
      const today = new Date().toISOString().split("T")[0];
      return {
        number_series: numberSeries?.data?.[0]?.next_series,
        entry_number: `${countData?.data?.count + 1}`,
        default_sales_account: companyData?.default_sales_account?.name,
        bill_number: formattedBillNumber,
        voucher_date: today,
        net_amount: 0,
        tax_rate: 13,
        vat_tax_amount: 0,
        total_amount: 0,
        items: [
          {
            account: "",
            quantity: "",
            rate: "",
            amount: "",
            discount: 0,
          },
        ],
      };
    },
  });
  const items = watch("items");
  const vat_tax = watch("tax_rate");

  useEffect(() => {
    if (!Array.isArray(items)) return;

    let taxableTotal = 0;
    let nonTaxableTotal = 0;
    let itemsValue = 0;
    let totalDiscount = 0;

    items.forEach((item, index) => {
      const amount = Number(item?.amount) || 0;
      const discount = Number(item?.discount) || 0;
      const isTaxable = Boolean(item?.is_taxable);
      itemsValue += amount;
      const amountAfterDiscount = Math.max(amount - discount, 0);
      totalDiscount += discount;
      if (isTaxable) {
        taxableTotal += amountAfterDiscount;
      } else {
        nonTaxableTotal += amountAfterDiscount;
      }
    });

    const netAmount = taxableTotal + nonTaxableTotal;
    const vatTax = taxableTotal * (Number(vat_tax) / 100);
    const totalAmount = netAmount + vatTax;

    setValue("items_value", itemsValue.toFixed(2));
    setValue("vat_tax_amount", vatTax.toFixed(2));
    setValue("total_amount", totalAmount.toFixed(2));
    setValue("taxable_amount", taxableTotal.toFixed(2));
    setValue("non_taxable_amount", nonTaxableTotal.toFixed(2));
    setValue("total_discount", totalDiscount.toFixed(2));
  }, [JSON.stringify(items), setValue, vat_tax]);

  const handleClosePrint = () => {
    setOpenPrintPopup(false);
    navigate("/accounting/sales");
  };

  const timeoutRef = useRef<any>(null);

  useEffect(() => {
    if (!Array.isArray(items)) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      items.forEach((item, index) => {
        const quantity = Number(item.quantity);
        const rate = Number(item.rate);
        const discount = Number(item.discount);
        const amount = Number(item.amount);
        const availableQuantity = Number(item.available_quantity);
        const maintainStock = item.is_maintain_stock;
        const minPurchaseRate = Number(item.last_purchase_price);

        let hasError = false;

        // Quantity check
        if (quantity > availableQuantity && maintainStock) {
          setError(`items.${index}.quantity`, {
            type: "manual",
            message: `Only ${availableQuantity} units available`,
          });
          hasError = true;
        } else {
          clearErrors(`items.${index}.quantity`);
        }

        // Rate check (without discount)
        if (rate < minPurchaseRate && rate) {
          setError(`items.${index}.rate`, {
            type: "manual",
            message: `Rate cannot be less than minimum purchase price (${minPurchaseRate})`,
          });
          hasError = true;
        } else {
          clearErrors(`items.${index}.rate`);
        }

        if (discount > 0) {
          const totalAmount = amount - discount;
          const discountedRate = totalAmount / quantity;
          if (discountedRate < minPurchaseRate) {
            setError(`items.${index}.discount`, {
              type: "manual",
              message: `Discounted rate (${discountedRate}) is below minimum purchase price (${minPurchaseRate})`,
            });
            hasError = true;
          } else {
            clearErrors(`items.${index}.discount`);
          }
        } else {
          clearErrors(`items.${index}.discount`);
        }
      });
    }, 500);

    return () => clearTimeout(timeoutRef.current);
  }, [JSON.stringify(items)]);

  return (
    <>
      <Card>
        <form
          onSubmit={handleSubmit((data) => handleCreate(data, false, setError))}
        >
          <Form
            control={control}
            register={register}
            errors={errors}
            getValues={getValues}
            companyData={companyData}
            isEdit={false}
            isCancel={false}
            setValue={setValue}
            clearErrors={clearErrors}
            setOpenUserFormPopup={setIsOpenModalPopup}
            setIsOpenItemsPopup={setIsOpenItemsPopup}
            watch={watch}
          />
          <div className={tw`w-full flex gap-x-4 justify-end px-4`}>
            <div className={tw`w-24 `}>
              <Button
                type="button"
                label="Save to Draft"
                isLoading={isLoading}
                onClick={handleSubmit((data) =>
                  handleCreate(data, true, setError)
                )}
              />
            </div>
            <div className={tw`w-24 `}>
              <Button label="Submit" isLoading={isLoading} />
            </div>
          </div>
        </form>
      </Card>
      <PopupModal
        isOpen={openPrintPopup}
        setIsOpen={handleClosePrint}
        title={"Print"}
      >
        <form action="#" className={tw`w-[600px]`}>
          <Button
            label={"Print "}
            type="button"
            isLoading={isPrintLoading}
            disabled={isPrintLoading}
            onClick={handlePrint}
            autoFocus
          />
          <Button
            type="button"
            label={"Close"}
            onClick={() => handleClosePrint()}
          />
        </form>
      </PopupModal>
      <PopupModal
        isOpen={isOpenModalPopup}
        setIsOpen={setIsOpenModalPopup}
        title="Create Party"
      >
        <CreateParty
          setOpenUserFormPopup={setIsOpenModalPopup}
          setParty={setValue}
        />
      </PopupModal>
      <PopupModal
        isOpen={isOpenItemsPopup}
        setIsOpen={setIsOpenItemsPopup}
        title="Create Items"
      >
        <AddInventoryPopup
          setOpenInventoryFormPop={setIsOpenItemsPopup}
          setOpenCategoryFormPopup={setOpenCategoryFormPopup}
          setOpenUnitFormPopup={setOpenUnitFormPopup}
          setItems={setValue}
        />
      </PopupModal>

      <PopupModal
        title="Create unit"
        isOpen={openUnitFormPopup}
        setIsOpen={setOpenUnitFormPopup}
      >
        <CreateUnit setOpenUnitFormPopup={setOpenUnitFormPopup} />
      </PopupModal>
      <PopupModal
        title="Create Category"
        isOpen={openCategoryFormPopup}
        setIsOpen={setOpenCategoryFormPopup}
      >
        <div className={tw`min-w-[400px]`}>
          <ProductGroupForm
            setOpenCategoryFormPopup={setOpenCategoryFormPopup}
          />
        </div>
      </PopupModal>
    </>
  );
};

export default AddSales;

